import * as React from 'react';
import { codes } from 'currency-codes';
import { Text, TextArea, Select, Checkbox } from '../../components/forms';
import Section from '../../components/Section';
import { Variant } from '../../product';
import Price from '../../components/forms/Price';
import HelpHint from '../../components/HelpHint';
import { ProfileContext } from '../../profile';
import { Contract } from '../../contract';

type Props = {
  variant: Variant;
  setVariant: Function;
};

const stockPolicies = () => [
  { label: 'Out of stock', value: 'out_of_stock' },
  { label: 'Continue', value: 'continue' },
  { label: 'Back Order', value: 'back_order' },
];

const setValue = (obj: Variant, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      [field]: newValue,
    }),
  );

const setPriceValue = (obj: Variant, onChange: Function) => (
  field: string,
  newValue: any,
) =>
  onChange(
    Object.assign({}, obj, {
      price: {
        ...obj.price,
        [field]: newValue,
      },
    }),
  );

const calculateSalePrice = (value: number, contract: Contract) => {
  if (!contract || !contract.is_active || contract.type === 'monthly_fee') {
    return value;
  }

  if (contract.amount > 0) {
    return value - contract.amount;
  }

  if (contract.percentage > 0) {
    return value - value * (contract.percentage / 100);
  }

  return value;
};

const formatPrice = (value: number) => {
  return (value / 100).toFixed(2);
};

export default function GeneralVariantInfo(props: Props) {
  const context = React.useContext(ProfileContext);
  const contract = context.profile.company.contract;
  const handleOnChange = setValue(props.variant, props.setVariant);
  const handlePriceChange = setPriceValue(props.variant, props.setVariant);

  return (
    <React.Fragment>
      <Section title="General">
        <div className="flex flex-wrap">
          <div className="flex-auto w-50-ns w-100 pr2">
            <Text
              label="Name"
              field={['name']}
              id="name"
              defaultValue={props.variant.name}
              onValueChange={handleOnChange}
            />
            <TextArea
              label="Description"
              field={['description_text']}
              id="description_text"
              defaultValue={props.variant.description_text}
              onValueChange={handleOnChange}
            />
            <Checkbox
              label="Active?"
              field={['available']}
              id="available"
              defaultValue={props.variant.available}
              onValueChange={handleOnChange}
            />
          </div>
          <div className="flex-auto w-50-ns w-100 pl2">
            <HelpHint>
              Ensure the description describes the unique feature of this
              product variant. For example: "T-shirt is has the following
              dimensions…"
            </HelpHint>
          </div>
        </div>
      </Section>
      <Section title="Inventory">
        <div className="flex flex-wrap">
          <div className="flex-auto w-50-ns w-100 pr2">
            <Text
              label="SKU (Stock Keeping Unit)"
              field={['sku']}
              id="sku"
              defaultValue={props.variant.sku}
              onValueChange={handleOnChange}
            />
            <Select
              label="Stock Policy"
              field={['stock_policy']}
              id="stock_policy"
              options={stockPolicies()}
              defaultValue={props.variant.stock_policy}
              onValueChange={handleOnChange}
            />
          </div>
          <div className="flex-auto w-50-ns w-100 pl2">
            <HelpHint>
              Stock Policy allows you to control of what happens when the
              product runs out of stock.
              <ul className="list pa0 mt4">
                <li>
                  `<strong>Out of Stock</strong>` — If Stock count reaches <strong>0</strong>,
                  customers will not be able to buy this product variant.
                </li>
                <li>
                  `<strong>Continue</strong>` — Customers will be able to
                  indefinitely add the product to their cart.
                </li>
                <li>
                  `<strong>Back Order</strong>` — This option is best if you are
                  relying on a third party to fullfil the order.
                </li>
              </ul>
            </HelpHint>
          </div>
        </div>
      </Section>
      <Section title="Pricing">
        <div className="flex flex-wrap">
          <div className="flex-auto w-50-ns w-100 pr2">
            <Price
              label="Retail Unit Price (With decimal point i.e. 8.00)"
              field={['value']}
              id="value"
              defaultValue={props.variant.price.value}
              onValueChange={handlePriceChange}
            />
            <Select
              label="Currency"
              field={['currency']}
              id="currency"
              options={codes().map((value: string) => ({
                value,
                label: value,
              }))}
              defaultValue={props.variant.price.currency}
              onValueChange={handlePriceChange}
            />
          </div>
          <div className="flex-auto w-50-ns w-100 pl2">
            <p className="lh-copy mv3 black b ttu">
              Retail price: {formatPrice(props.variant.price.value)}{' '}
              {props.variant.price.currency}
            </p>
            <p className="lh-copy mv3 white-70">
              Customers will be able to place orders using the Filtru app.
            </p>
            <p className="lh-copy mv3 green b ttu">
              Paid to you:{' '}
              {formatPrice(
                calculateSalePrice(props.variant.price.value, contract),
              )}{' '}
              {props.variant.price.currency}
            </p>
            <p className="lh-copy mv3 white-70">
              The prices are calculated according to the Contract Terms agreed
              upon registering for a business account. All arrears will be paid
              out on the date specified in Contract Terms.
            </p>
          </div>
        </div>
      </Section>
    </React.Fragment>
  );
}
